import { usePaginationFragment, Variables } from 'react-relay'
import { MutableRefObject, startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useDetectChange } from 'hooks/state'
import { useLogger } from 'hooks/logger'
import { useDebouncedCallback } from 'use-debounce'

/**
 * Automatically refetches only when the filters change, with debounce
 * @param refetch the function to call to refetch
 * @param filters the filters (memoize this!)
 * @param delay delay to use (optional, to override the default)
 */
export function useRefetchOnFiltersChange<T extends Object>(
  refetch: (variables: T) => void,
  filters: T,
  delay?: number,
  defaultFilters: Partial<T> = {},
) {
  const callback = useCallback((filtersDebounced: T) => {
    refetch(filtersDebounced)
  }, [refetch])
  useDetectChange(filters, delay, callback, defaultFilters)
}

// Tweak the following constants to improve the user experience
export const DEFAULT_PER_PAGE = 10
const INFINITE_SCROLL_HEIGHT_MULTIPLIER = 1.45
const LOAD_NEXT_PAGE_DEBOUNCE_MS = 300
const LOAD_NEXT_PAGE_MAX_WAIT_MS = 1000
const BUTTON_BOUNDS_CHECK_FREQUENCY_MS = 150

// The following constants should not be changed
const DEFAULT_FILTERS = {}

type PaginationFields = 'hasNext' | 'loadNext' | 'isLoadingNext' | 'isLoadingPrevious' | 'refetch'

export type RelayPaginationData = Pick<ReturnType<typeof usePaginationFragment>, PaginationFields>

export interface IRelayPaginationOptions<Filters = {}> extends RelayPaginationData {
  perPage?: number
  firstPage?: number
  currentCount: number
  totalCount?: number | null | undefined
  /**
   * set this to the filter value that was used when fetching the initial fragment
   */
  defaultFilters?: Filters
  /**
   * set this to the currently selected filters
   */
  filters?: Filters
  noWrapFilters?: boolean
  filtersDebounce?: number
  infiniteScroll?: boolean
  stream?: boolean
  streamStep?: number
  disableRefetch?: boolean
  fetchKey?: number | string
  ref?: MutableRefObject<HTMLElement>
}

/**
 * @param props.refetch refetch function from usePaginationFragment; the fragment MUST define the 'count' argument
 */
export function useRelayPagination<Filters = {}>(props: IRelayPaginationOptions<Filters>) {
  const {
    // Relay specific props
    refetch: refetchRelay,
    hasNext,
    loadNext,
    isLoadingNext,
    isLoadingPrevious,
    // Custom props
    currentCount = 0,
    totalCount: totalCountProp,
    defaultFilters = DEFAULT_FILTERS,
    filters = defaultFilters,
    perPage = DEFAULT_PER_PAGE,
    firstPage,
    noWrapFilters = false,
    filtersDebounce = 400,
    infiniteScroll = true,
    stream = true,
    streamStep = 1,
    disableRefetch = false,
    fetchKey,
    ref,
  } = props

  const logger = useLogger('useRelayPagination')
  const loading = isLoadingNext || isLoadingPrevious
  const totalCount = Math.max(currentCount || 0, totalCountProp || 0, 0)
  const moreCount = totalCount && currentCount ? (totalCount - currentCount) : null
  const nextLoadCount = moreCount ? Math.min(moreCount, perPage) : null
  const empty = !currentCount || currentCount < 0
  const [targetCount, setTargetCount] = useState(currentCount || firstPage || perPage)
  const [boundsCheckIntervalHandle, setBoundsCheckIntervalHandle] = useState<number | null>(null)

  const isStreaming = stream && targetCount > currentCount && (isLoadingNext || hasNext)

  const checkIfNeedToAutoLoadNextPage = () => {
    if (hasNext && !loading && targetCount > currentCount) {
      startTransition(() => {
        const amount = stream ? streamStep : perPage
        logger.debug('loadNext called with', amount)
        loadNext(amount)
      })
    }
  }

  useEffect(() => {
    checkIfNeedToAutoLoadNextPage()
  }, [hasNext, loading, targetCount, currentCount, stream, streamStep, perPage, loadNext])

  // TODO: figure out where the following random error is coming from:
  // Warning: Cannot update a component (`TeamList`) while rendering a different component (`RelayPagination`)
  // To locate the bad setState() call inside `RelayPagination`
  // The error can be reproduced by doing bulk updates in e2e tests, it happens randomly.

  const refetch = useCallback((newFilters: Partial<Filters>) => {
    if (disableRefetch) return null

    // TODO1: remove 'count' and use 'first' everywhere
    // TODO2: remove 'noWrapFilters' and instead make consumer code responsible for passing the correct filters
    // TODO3: add a check to make sure the specified fragment defines 'first' as an argument
    const payload: Partial<Variables> = noWrapFilters ? { first: targetCount, count: targetCount, ...newFilters } : { first: targetCount, count: targetCount, filters: newFilters }

    logger.debug('refetch')
    startTransition(() => {
      refetchRelay(
        payload,
        {
          // note: 'network-only' is fine here and makes sense
          // at this point, we are doing a force-refetch, and want to get the latest remote data anyways
          // we don't care about the cache
          fetchPolicy: 'network-only',
        },
      )
    })
  }, [targetCount, refetchRelay, noWrapFilters, disableRefetch])

  const handleFetchKeyChange = useCallback(() => {
    logger.debug('refetch due to fetchkey change')
    refetch(filters)
  }, [refetch, filters])

  useDetectChange(filters, filtersDebounce, refetch, defaultFilters)
  useDetectChange(fetchKey, filtersDebounce, handleFetchKeyChange)

  const loadNextPageImmediate = () => {
    if (currentCount >= targetCount) {
      const newTargetCount = targetCount + (nextLoadCount ?? perPage)
      logger.debug('loadNextPage called: changing target count from', targetCount, 'to', newTargetCount)
      startTransition(() => {
        setTargetCount(newTargetCount)
      })
    }
  }

  const loadNextPage = useDebouncedCallback(loadNextPageImmediate, LOAD_NEXT_PAGE_DEBOUNCE_MS, {
    maxWait: LOAD_NEXT_PAGE_MAX_WAIT_MS,
    leading: true,
  })

  // Infinite-scrolling implementation, it simply triggers load if the button is in view (or almost in view)
  useEffect(() => {
    if (boundsCheckIntervalHandle) window.clearInterval(boundsCheckIntervalHandle)

    setBoundsCheckIntervalHandle(window.setInterval(() => {
      if (infiniteScroll && !isStreaming && ref?.current && hasNext && !loading) {
        const bounds = ref.current.getBoundingClientRect()

        if (bounds.top < window.innerHeight * INFINITE_SCROLL_HEIGHT_MULTIPLIER) {
          logger.debug('detected that the button is in view, calling loadNextPage')
          loadNextPage()
        }
      }
    }, BUTTON_BOUNDS_CHECK_FREQUENCY_MS))

    return () => {
      if (boundsCheckIntervalHandle) {
        window.clearInterval(boundsCheckIntervalHandle)
      }
    }
  }, [perPage, ref?.current, infiniteScroll, isStreaming, hasNext, loading, loadNextPage])

  return useMemo(() => ({
    moreCount,
    nextLoadCount,
    loading,
    empty,
    isStreaming,
    infiniteScroll,
    loadNextPage,
  }), [moreCount, nextLoadCount, loading, empty, isStreaming, infiniteScroll, loadNextPage])
}

type IUseLoadAllProps = Pick<RelayPaginationData, 'hasNext' | 'loadNext' | 'isLoadingNext'> & {
  perPage?: number
}

export function useLoadAll({ hasNext, loadNext, isLoadingNext, perPage = 20 }: IUseLoadAllProps) {
  const logger = useLogger('useLoadAll')

  useEffect(() => {
    logger.debug('Checking if should load next...', hasNext, isLoadingNext)
    if (hasNext && !isLoadingNext) {
      logger.debug('Loading next', perPage)
      loadNext(perPage)
    }
  }, [hasNext, isLoadingNext, perPage])

  return {
    loading: Boolean(isLoadingNext || hasNext),
  }
}
